import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Bike, Clock, CreditCard, CheckCircle2, XCircle, Loader2, ShieldCheck, Timer, Landmark } from "lucide-react";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const fmtDT = (v) => { try { return new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return v; } };
const METODO_META = {
  bonifico: { label: "Bonifico bancario", color: "#2b6cb0" },
  satispay: { label: "Satispay", color: "#EE4036" },
  nexi: { label: "Nexi XPay", color: "#00337a" },
  paypal: { label: "PayPal", color: "#0070BA" },
};

const STATO_LABEL = {
  in_attesa_caparra: "In attesa del pagamento caparra",
  confermata: "Confermata", scaduta: "Scaduta", annullata: "Annullata",
  in_corso: "In corso", concluso: "Conclusa",
};

export default function Permalink() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  const [bonifico, setBonifico] = useState(null);

  const load = async () => {
    try { setData((await api.get(`/public/booking-link/${token}`)).data); }
    catch (e) { setError(e.response?.status === 404 ? "Prenotazione non trovata." : "Errore di caricamento."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const pay = async (metodo) => {
    setPaying(metodo || true);
    try {
      const r = await api.post(`/public/booking-link/${token}/pay`, metodo ? { metodo } : {});
      if (r.data.redirect && r.data.redirect_url) { window.location.assign(r.data.redirect_url); return; }
      if (r.data.bonifico) setBonifico(r.data.bonifico);
      await load();
    }
    catch (e) { setError(e.response?.data?.detail || "Pagamento non riuscito."); }
    finally { setPaying(false); }
  };

  if (loading) return <div className="min-h-screen bg-stone-100 flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#f98125]" /></div>;
  if (error) return <div className="min-h-screen bg-stone-100 flex items-center justify-center p-6"><div className="bg-white rounded-xl shadow p-8 text-center max-w-sm"><XCircle className="h-10 w-10 text-red-500 mx-auto mb-3" /><p className="text-stone-700">{error}</p></div></div>;

  const isPending = data.stato === "in_attesa_caparra";
  const isConfirmed = data.stato === "confermata";
  const isDead = ["scaduta", "annullata"].includes(data.stato);

  return (
    <div className="min-h-screen bg-stone-100 py-10 px-4" data-testid="permalink-page">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 text-[#f98125] font-bold text-xl"><Bike className="h-6 w-6" /> {data.app_name}</div>
          <p className="text-stone-500 text-sm mt-1">Riepilogo prenotazione</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className={`px-5 py-3 text-sm font-semibold flex items-center gap-2 ${isConfirmed ? "bg-[#39FF14]/15 text-green-700" : isDead ? "bg-red-50 text-red-600" : "bg-[#f98125]/10 text-[#b85e10]"}`} data-testid="permalink-stato">
            {isConfirmed ? <CheckCircle2 className="h-4 w-4" /> : isDead ? <XCircle className="h-4 w-4" /> : <Timer className="h-4 w-4" />}
            {STATO_LABEL[data.stato] || data.stato}
          </div>
          <div className="p-5 space-y-4">
            {data.cliente_nome && <div className="text-stone-700">Ciao <b>{data.cliente_nome}</b>,</div>}
            <div>
              <div className="text-xs uppercase tracking-wider text-stone-400 mb-1">Bici</div>
              {data.bici.map((b, i) => <div key={i} className="text-sm text-stone-700">Bici {b.numero}{b.modello ? ` — ${b.modello}` : ""}</div>)}
            </div>
            <div className="flex items-center gap-2 text-sm text-stone-600"><Clock className="h-4 w-4 text-[#f98125]" /> {fmtDT(data.inizio)} → {fmtDT(data.fine)}</div>
            <div className="border-t border-stone-100 pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-stone-600"><span>Totale noleggio</span><span>{eur(data.totale)}</span></div>
              <div className="flex justify-between text-stone-600"><span>Cauzione (in sede)</span><span>{eur(data.cauzione)}</span></div>
              <div className="flex justify-between font-semibold text-stone-900"><span>Caparra da pagare ora</span><span data-testid="permalink-caparra">{eur(data.caparra)}</span></div>
              <div className="flex justify-between text-xs text-stone-400"><span>Saldo alla consegna</span><span>{eur(data.saldo_in_sede)}</span></div>
            </div>

            {isPending && !bonifico && (
              <>
                {data.hold_scadenza && <div className="text-xs text-stone-400 text-center">Prenotazione valida fino al {fmtDT(data.hold_scadenza)}</div>}
                <div className="text-xs uppercase tracking-wider text-stone-400 flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-[#f98125]" /> Scegli come pagare la caparra</div>
                {(data.metodi_pagamento || []).length === 0 && (
                  <div className="text-sm text-stone-500 bg-stone-50 border border-stone-200 rounded-lg p-4" data-testid="permalink-no-methods">Nessun metodo di pagamento è al momento disponibile. Contatta lo staff.</div>
                )}
                <div className="space-y-2.5">
                  {(data.metodi_pagamento || []).map((m) => {
                    const meta = METODO_META[m.key] || { label: m.nome || m.key, color: "#f98125" };
                    const preferred = data.metodo_preferito && data.metodo_preferito === m.key;
                    return (
                      <button key={m.key} onClick={() => pay(m.key)} disabled={!!paying} data-testid={`permalink-pay-${m.key}`}
                        className={`w-full flex items-center justify-center gap-2 text-white font-bold py-3 rounded-lg transition-opacity disabled:opacity-60 relative ${preferred ? "ring-2 ring-offset-2 ring-[#f98125]" : ""}`}
                        style={{ background: meta.color }}>
                        {paying === m.key ? <Loader2 className="h-5 w-5 animate-spin" /> : (m.key === "bonifico" ? <Landmark className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />)}
                        {m.key === "bonifico" ? "Paga con bonifico" : `Paga con ${meta.label}`}
                        {preferred && <span className="absolute -top-2 -right-2 bg-[#f98125] text-[10px] font-bold px-2 py-0.5 rounded-full">consigliato</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="text-center text-xs text-stone-400">Caparra: <b className="text-stone-700">{eur(data.caparra)}</b></div>
              </>
            )}
            {bonifico && (
              <div className="border border-stone-200 rounded-lg p-4 bg-stone-50 text-sm space-y-2" data-testid="permalink-bonifico">
                <div className="font-semibold text-stone-800">Dati per il bonifico</div>
                <div className="flex justify-between gap-3"><span className="text-stone-500">Intestatario</span><span className="text-right">{bonifico.intestatario || "—"}</span></div>
                <div className="flex justify-between gap-3"><span className="text-stone-500">IBAN</span><span className="font-mono text-right break-all">{bonifico.iban}</span></div>
                <div className="flex justify-between gap-3"><span className="text-stone-500">Causale</span><span className="text-right">{bonifico.causale}</span></div>
                <div className="flex justify-between gap-3"><span className="text-stone-500">Importo</span><span>{eur(bonifico.importo)}</span></div>
                <div className="text-[11px] text-stone-400 pt-1">La prenotazione sarà confermata alla ricezione del bonifico.</div>
              </div>
            )}
            {isConfirmed && <div className="text-center text-green-700 text-sm font-medium flex items-center justify-center gap-2"><CheckCircle2 className="h-5 w-5" /> Caparra pagata · prenotazione confermata!</div>}
            {isDead && <div className="text-center text-red-500 text-sm">Questa prenotazione non è più valida.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
